import React from 'react';
import { ChevronDown } from 'lucide-react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';

const FAQ = () => {
  const faqs = [
    {
      question: 'What services does Impetus Investment Services provide?',
      answer: 'Impetus Investment Services is an AMFI-registered Mutual Fund Distributor (ARN: 116347). We provide mutual fund distribution and investor assistance, including scheme information, investment facilitation and periodic portfolio reviews. Other products such as fixed deposits, insurance, bonds and real estate are arranged through their respective providers and are not covered by our AMFI registration.'
    },
    {
      question: 'Are you a SEBI-registered Investment Adviser?',
      answer: 'No. We are an AMFI-registered Mutual Fund Distributor (ARN: 116347). We do not provide investment advisory services under the SEBI (Investment Advisers) Regulations, 2013.'
    },
    {
      question: 'How are you compensated?',
      answer: 'We may receive commissions from Asset Management Companies for investments made through Regular Plans of mutual fund schemes. Investors may also invest through Direct Plans, which have a lower expense ratio and do not involve distributor commission. See our Disclosures & Investor Grievances page for details.'
    },
    {
      question: 'How can I start investing in mutual funds?',
      answer: 'You can start by booking an appointment with our team. We will understand your financial goals, risk profile, and investment horizon, and then share information on mutual fund schemes that may suit you. You can invest through SIP (starting from as low as ₹500 per month) or lump-sum.'
    },
    {
      question: 'What documents are required to invest?',
      answer: 'You will need: PAN Card, Aadhaar Card, Bank account details, Passport-size photograph, and Cancelled cheque. For online investments, you may also need to complete KYC (Know Your Customer) verification.'
    },
    {
      question: 'What is a SIP?',
      answer: 'SIP (Systematic Investment Plan) is a method of investing a fixed amount regularly in mutual funds. It helps in rupee cost averaging and builds investment discipline. You can start a SIP with amounts as low as ₹500 per month.'
    },
    {
      question: 'Is there a minimum amount required to invest?',
      answer: 'For SIP investments, you can start with as low as ₹500 per month depending on the fund. For lump-sum investments, most funds have a minimum of ₹5,000. However, the suitable investment amount depends on your financial goals and capacity.'
    },
    {
      question: 'Are mutual fund returns guaranteed?',
      answer: 'No, mutual fund returns are not guaranteed. Mutual fund investments are subject to market risks. Returns depend on market performance and fund management. Past performance may or may not be sustained in future and is not a guarantee of future returns.'
    },
    {
      question: 'How often should my portfolio be reviewed?',
      answer: 'We recommend reviewing your portfolio at least once a year, or whenever there is a significant change in your financial situation, goals, or risk profile. Regular reviews help ensure your investments remain aligned with your objectives.'
    },
    {
      question: 'Can you review my existing mutual fund portfolio?',
      answer: 'Yes, we offer portfolio review services. We analyze your existing investments for overlap, diversification, alignment with goals, and performance. Based on the review, we share observations and options for any changes that may be needed.'
    },
    {
      question: 'Can I book an online consultation?',
      answer: 'Yes, we offer video consultations, phone consultations, and in-person meetings. You can select your preferred consultation type when booking an appointment through our website.'
    },
    {
      question: 'How does an SWP work?',
      answer: 'SWP (Systematic Withdrawal Plan) allows you to withdraw a fixed amount at regular intervals from your mutual fund investment. It is commonly used for generating regular income, especially during retirement, while keeping your remaining investment growing.'
    },
    {
      question: 'What is the difference between equity, debt and hybrid funds?',
      answer: 'Equity funds invest primarily in stocks and are suitable for long-term goals with higher risk tolerance. Debt funds invest in fixed-income securities and are relatively lower risk. Hybrid funds invest in both equity and debt, offering a balanced approach.'
    },
    {
      question: 'How do I choose funds based on my risk profile?',
      answer: 'Your risk profile depends on your age, investment horizon, financial goals, income stability, and ability to handle market volatility. We assess your risk profile during consultation and share information on fund categories that may be suitable.'
    },
    {
      question: 'Can I reschedule my appointment?',
      answer: 'Yes, you can reschedule your appointment by contacting us via phone, email, or WhatsApp. We request at least 24 hours notice for rescheduling.'
    },
    {
      question: 'How can existing clients contact the service team?',
      answer: 'Existing clients can reach us through phone (98417 90000 / 73058 35581), email (cs@impetusinvestments.in), or WhatsApp. You can also schedule a follow-up appointment through our website.'
    },
    {
      question: 'How can I raise a complaint?',
      answer: 'Please email cs@impetusinvestments.in or call us first. If your complaint is not resolved, you may approach the respective AMC, lodge a complaint on SEBI SCORES (scores.sebi.gov.in) or use the SMART ODR portal (smartodr.in). Details are on our Disclosures & Investor Grievances page.'
    }
  ];

  return (
    <div className="min-h-screen" data-testid="faq-page">
      <section className="pt-32 pb-20 px-4 sm:px-6 lg:px-8 bg-[#FDF8F5]">
        <div className="max-w-7xl mx-auto text-center">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-medium text-[#A0451E] mb-6" data-testid="faq-heading">
            Frequently Asked Questions
          </h1>
          <p className="text-base font-body text-[#4A4A4A] max-w-3xl mx-auto leading-relaxed">
            Find answers to common questions about our services, mutual fund investing, and how we can help you achieve your financial goals.
          </p>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Accordion type="single" collapsible className="space-y-4">
            {faqs.map((faq, index) => (
              <AccordionItem
                key={index}
                value={`item-${index}`}
                className="bg-[#FDF8F5] rounded-xl border border-[#E5E5E5] px-6"
                data-testid={`faq-item-${index}`}
              >
                <AccordionTrigger className="text-left font-heading font-medium text-[#A0451E] hover:text-[#D97706] py-6">
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent className="text-sm font-body text-[#4A4A4A] leading-relaxed pb-6">
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      <section className="py-20 section-bg-secondary">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-2xl sm:text-3xl font-heading font-medium text-[#A0451E] mb-6">
            Still Have Questions?
          </h2>
          <p className="text-base font-body text-[#4A4A4A] mb-8 leading-relaxed">
            Our team is here to help. Schedule a consultation or reach out to us directly.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/appointment" className="btn-primary inline-block" data-testid="faq-book-appointment-btn">
              Book an Appointment
            </a>
            <a href="/contact" className="btn-secondary inline-block" data-testid="faq-contact-us-btn">
              Contact Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQ;